/**
 * Inbox view membership. Views are derived from ticket state, never stored —
 * a denormalised tag goes stale the moment a status or assignee changes.
 */
export type SupportView =
  /** Every open ticket, assigned or not. No longer a sidebar tab (Neel's
   *  V2.3 item 7 — the default is Unassigned now); it survives as the
   *  predicate the per-channel rows scope with, so "Email" still means
   *  "every open email", not "every unassigned email". */
  "all-open" | "mine" | "unassigned" | "waiting" | "urgent" | "archived";
export type SupportChannel = "ticket" | "email" | "whatsapp";

export interface InboxTicket {
  id: string;
  status: string;
  channel: SupportChannel;
  priority: string | null;
  assigned_to: string | null;
  archived_at: string | null;
  reported_by: string | null;
  contact_type?: "tenant" | "owner" | "external";
}

export const OPEN_STATUSES = ["open", "in_progress", "waiting"];

export function matchesView(
  ticket: InboxTicket,
  view: SupportView,
  currentUserId: string | null
): boolean {
  const archived = !!ticket.archived_at;
  if (view === "archived")
    return archived || !OPEN_STATUSES.includes(ticket.status);
  if (archived) return false;
  const isOpen = OPEN_STATUSES.includes(ticket.status);
  switch (view) {
    case "all-open":
      return isOpen;
    case "mine":
      return (
        isOpen && currentUserId != null && ticket.assigned_to === currentUserId
      );
    case "unassigned":
      return isOpen && !ticket.assigned_to;
    case "waiting":
      return ticket.status === "waiting";
    case "urgent":
      return isOpen && ticket.priority === "urgent";
    default: {
      const _exhaustive: never = view;
      return false;
    }
  }
}

export const SUPPORT_VIEWS: {
  key: SupportView;
  i18nKey: string;
  fallback: string;
}[] = [
  { key: "mine", i18nKey: "support.view.mine", fallback: "Assigned to me" },
  {
    key: "unassigned",
    i18nKey: "support.view.unassigned",
    fallback: "Unassigned",
  },
  {
    key: "waiting",
    i18nKey: "support.view.waiting",
    fallback: "Waiting on tenant",
  },
  { key: "urgent", i18nKey: "support.view.urgent", fallback: "Urgent" },
];

/** What a list row renders. `InboxTicket` is deliberately the narrower shape
 *  the predicates need, so `matchesView` stays testable without fixtures.
 *
 *  The fields below `user_id` are genuinely present on every row
 *  `useSupportInbox` returns (that hook's `select("*")` plus a booking/
 *  property/room embed spreads the full `issues` row through), so they
 *  belong on the contract rather than behind an unchecked cast at the
 *  component that reads them — an unchecked `as` gives no warning if an
 *  embed key is ever renamed; a missing property here does. */
export interface InboxTicketRow extends InboxTicket {
  title: string;
  tenantName: string;
  user_id: string | null;
  /** Resolved display name of the assignee, null when unassigned. Always
   *  populated once assigned (falls back to email, then a translated
   *  "Unknown admin") — never left for the UI to guess at from a raw id. */
  assignedName: string | null;
  /** Real per-ticket unread count (see useSupportInbox.ts's mirror of
   *  the old Kanban console's get_issue_unread_counts fetch) — always a number,
   *  0 when nothing is unread, never a stand-in for "not fetched yet". */
  unread_count: number;
  team_unread_count?: number;
  /** `issues.*` columns and embeds that ride along via `select("*")` but
   *  that no view predicate needs — display-only, hence optional. */
  created_at?: string | null;
  description?: string | null;
  latestMessage?: string | null;
  latestMessageAt?: string | null;
  support_read_at?: string | null;
  metadata?: unknown;
  properties?: { title?: string | null } | null;
  rooms?: { title?: string | null } | null;
}

/** The address an email-sourced ticket came from — `issues.metadata.from_email`,
 *  written by inbound-email-webhook and the unknown-sender link flow. For an UNOWNED
 *  ticket (sender matched no user, so `reported_by` is NULL —
 *  20260914100000_issues_unowned_tickets.sql) there is no profile to hydrate a
 *  name from; this is the honest label in its place. Display-only: the edge
 *  side (_shared/inbound-email.ts's inboundSenderEmail) is the one that has to
 *  validate it as an address before mailing it. */
export function inboundSenderEmail(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata))
    return null;
  const raw = (metadata as { from_email?: unknown }).from_email;
  const email = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  return email.includes("@") ? email : null;
}

/** Hand-copied from supabase/functions/_shared/whatsapp.ts's `normalisePhone`
 *  — shipped src/ code never imports across the src/ ↔ supabase/functions/
 *  boundary (same reasoning as broadcast-audience.ts's mirror). Pinned by
 *  support-views.parity.test.ts, which imports the edge module at runtime to
 *  prove the two copies agree: drift here means the inbox displays a
 *  different number than matchPhoneToTenant actually matched on. */
const DEFAULT_COUNTRY_CODE = "49";
const MIN_PHONE_DIGITS = 8;
function normalisePhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/[^0-9]/g, "");
  if (!digits) return null;
  const out = digits.startsWith("00")
    ? digits.slice(2)
    : digits.startsWith("0")
      ? DEFAULT_COUNTRY_CODE + digits.slice(1)
      : digits;
  return out.length >= MIN_PHONE_DIGITS ? out : null;
}

/** The number a WhatsApp-sourced ticket came from — `issues.metadata.from_phone`,
 *  written by whatsapp-webhook (mirrors `inboundSenderEmail` above for the
 *  email side). Normalised for display so an unowned ticket's label agrees
 *  with the digits matchPhoneToTenant actually compared — the webhook itself
 *  stores the raw `wa_id` verbatim (case B of Task 6 relies on that being
 *  stable for re-matching), so this function, not the write path, is where
 *  the normalisation belongs. */
export function inboundSenderPhone(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata))
    return null;
  const raw = (metadata as { from_phone?: unknown }).from_phone;
  return typeof raw === "string" ? normalisePhone(raw) : null;
}

/**
 * Hand-copied from supabase/functions/_shared/whatsapp.ts's `serviceWindow`
 * (constants and regex included) — shipped src/ code never imports across
 * the src/ ↔ supabase/functions/ boundary (same reasoning as normalisePhone
 * above). Pinned by support-views.parity.test.ts, which imports the edge
 * module at runtime to prove the two copies agree: drift here means the
 * composer could offer a free-form WhatsApp reply the server is about to
 * refuse with `window_closed`, or hide the box for a ticket the server would
 * actually still accept a free-form reply on.
 *
 * Meta accepts a free-form WhatsApp message only within 24 hours of the
 * tenant's most recent message; outside that window only a pre-approved
 * template may be sent (send-ticket-reply/index.ts). `lastTenantMessageAt`
 * is the newest tenant-scoped comment's created_at on the ticket — never a
 * stored column, so the composer derives it from the same `issue_comments`
 * rows it already fetched for the thread (IssueChatThread.tsx), exactly as
 * send-ticket-reply/index.ts derives it from its own query.
 */
export const SERVICE_WINDOW_MS = 24 * 60 * 60 * 1000;

export type WhatsAppWindowState =
  | { open: true; expiresAt: string }
  | { open: false; reason: "never" | "expired"; expiredAt: string | null };

// A bare "YYYY-MM-DDTHH:mm:ss" (or a space instead of "T") has no explicit
// UTC offset, so JS reads it as *local* time — the exact same stored value
// would open or close the window differently depending on the browser's own
// TZ. Real callers (Postgres timestamptz, re-encoded as ISO) always carry an
// explicit "Z" or "+HH:MM", so requiring one here never narrows a
// legitimate input, only a malformed or ambiguous one.
const OFFSET_QUALIFIED_TIMESTAMP_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

export function serviceWindow(
  lastTenantMessageAt: string | null,
  now: Date
): WhatsAppWindowState {
  const last =
    lastTenantMessageAt !== null &&
    OFFSET_QUALIFIED_TIMESTAMP_RE.test(lastTenantMessageAt)
      ? new Date(lastTenantMessageAt).getTime()
      : NaN;
  if (Number.isNaN(last))
    return { open: false, reason: "never", expiredAt: null };

  // Clamp a last-message time that is after `now` (clock skew) so it can
  // never push the window further into the future than a message arriving
  // exactly now would — erring open is the dangerous direction here.
  const effectiveLast = Math.min(last, now.getTime());
  const expiresAt = new Date(effectiveLast + SERVICE_WINDOW_MS);
  return now.getTime() < expiresAt.getTime()
    ? { open: true, expiresAt: expiresAt.toISOString() }
    : { open: false, reason: "expired", expiredAt: expiresAt.toISOString() };
}

export const SUPPORT_CHANNELS: {
  key: SupportChannel;
  i18nKey: string;
  fallback: string;
}[] = [
  { key: "email", i18nKey: "support.channel.email", fallback: "Email" },
  {
    key: "whatsapp",
    i18nKey: "support.channel.whatsapp",
    fallback: "WhatsApp",
  },
];

/**
 * Does a reply on this channel ever produce a delivery receipt?
 *
 * Mirrors planReplyDispatch in supabase/functions/_shared/ticket-reply.ts,
 * whose dispatch branch is `channel === "email" || channel === "whatsapp"`.
 * Only those two reach a provider, so only those two ever stamp
 * `delivered_at` / `delivery_error`.
 *
 * Every other channel is portal-only: the tenant reads the comment straight
 * out of issue_comments, so it IS delivered the moment it is written and
 * there is no receipt to wait for. Treating those rows as "unconfirmed"
 * would mark every portal message pending forever.
 */
export function channelProducesDeliveryReceipt(
  channel: SupportChannel | null | undefined
): boolean {
  return channel === "email" || channel === "whatsapp";
}

/**
 * The human-readable text of a stored message body.
 *
 * An attachment-carrying body is stored as `CHATENV:{...}` JSON (the composer
 * writes it, and since inbound email attachments were added, so does
 * inbound-email-webhook — which is how a raw envelope first reached
 * `issues.description`). Every surface that renders or searches a stored body
 * has to unwrap it, or it shows the JSON to a human.
 *
 * Deliberately text-only and total: a body that is not an envelope, or is a
 * malformed one, returns unchanged. Callers that also need the attachments
 * decode the envelope themselves.
 */
export function envelopeText(raw: string | null | undefined): string {
  const body = raw ?? "";
  if (!body.startsWith("CHATENV:")) return body;
  try {
    const env = JSON.parse(body.slice(8)) as { text?: string };
    return env.text ?? "";
  } catch {
    return body;
  }
}
